import {useEffect, useState} from "react";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import type {IAppError} from "@pg-mono/request-state";
import {EmailInputWithVerification} from "@pg-rp/email-with-verification";
import {useEmailVerificationApiError} from "@pg-rp/email-with-verification/src/hooks/use_email_verification_api_error";

import {LeadButtonTerms} from "../../../application/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../application/components/LeadRodoTerms";
import {useVerifyEmailQuery} from "../../../email_verification/api/email_verification_api";
import {addNotification} from "../../../notifications/state/notifications_slice";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {userSignUpHit} from "../../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../../tracking/types/AmplitudeAuthPlacement";
import {UserMktOptins} from "../../../user/components/UserMktOptins";
import {UserAccountType} from "../../../user/types/UserAccountType";
import {authSucceeded} from "../../actions/auth_succeeded_event";
import {emailSignUp} from "../../api/email_sign_up";
import {registerFormDefaultValues} from "../../constants/register_form_default_values";
import {registerFormValidationSchema} from "../../constants/register_form_validation_schema";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../../tracking/auth_analytics";
import {trackAmplitudeAuthEvent} from "../../tracking/track_amplitude_auth_event";
import {AuthActionType} from "../../types/AuthActionType";
import type {IRegister} from "../../types/IRegister";

interface IProps {
    onSuccessfulSubmit?: () => void;
    userSource: UserSource;
    gtmSourceSection?: string;
    gtmEventData?: () => void;
    amplitudePlacement?: AmplitudeAuthPlacement | null;
}

const registerButtonText = "Zarejestruj się";

export const RegisterForm = (props: IProps) => {
    const {onSuccessfulSubmit, userSource, gtmSourceSection, gtmEventData, amplitudePlacement} = props;
    const dispatch = useAppDispatch();

    const [email, setEmail] = useState<string | null>(null);
    const {data, error, isLoading, isFetching} = useVerifyEmailQuery({email: email || ""}, {skip: !email});
    const apiError = useEmailVerificationApiError({errorData: error});
    const currentViewType = useAppSelector((state) => state.viewType.current);
    const countryCodes = useAppSelector((state) => state.variables.data?.country_codes ?? []);

    const methods = useFormRHF<IRegister>({
        defaultValues: registerFormDefaultValues,
        resolver: yupResolver(registerFormValidationSchema),
        mode: "onChange"
    });
    const ignoreVerification = !!methods.formState.defaultValues?.email && !email;
    const disableSubmitButton = (!ignoreVerification && !data) || methods.formState.isSubmitting || !methods.formState.isValid || isLoading || isFetching;

    const marketingSms = methods.watch("marketing_sms");

    useEffect(() => {
        if (!apiError.isValid) {
            methods.setError("email", {message: apiError.message});
        }
    }, [apiError]);

    const onSubmit = async ({email, marketing_email, marketing_sms, password, phone}: IRegister) => {
        await emailSignUp({
            email,
            password,
            ...(marketing_sms ? {phone: `${phone.code} ${phone.number}`} : {}),
            type: UserAccountType.LOCAL,
            marketing_sms,
            marketing_email
        })
            .then(async (response) => {
                authAnalytics.gtm.emailAuthEvent({
                    event: GAAuthEventName.SIGN_UP,
                    eventCategory: GAAuthEventCategory.REGISTRATION_FORM,
                    source: gtmSourceSection ?? null,
                    userSource
                });

                dispatch(
                    authSucceeded({
                        type: AuthActionType.REGISTRATION,
                        userSource,
                        gtmSourceSection,
                        registrationMethod: "email"
                    })
                );
                if (amplitudePlacement) {
                    trackAmplitudeAuthEvent("signed_up", {signUpMethod: "internal", signUpPlacement: amplitudePlacement});
                }
                userSignUpHit(userSource, response, currentViewType);
                gtmEventData?.();
                onSuccessfulSubmit?.();
            })
            .catch((error: {appError?: IAppError}) => {
                if (error.appError?.fieldErrors) {
                    for (const field of Object.keys(error.appError.fieldErrors)) {
                        const message = error.appError.fieldErrors[field];

                        methods.setError(field as keyof IRegister, {
                            message: Array.isArray(message) ? message.join(", ") : message
                        });
                    }
                }

                if (error.appError?.nonFieldErrors && error.appError.nonFieldErrors.length) {
                    dispatch(addNotification({notification: {content: error.appError.nonFieldErrors.join(" "), type: "failure"}}));
                }
            });
    };

    return (
        <FormProviderRHF {...methods}>
            <form className={form} onSubmit={methods.handleSubmit(onSubmit)}>
                <EmailInputWithVerification verificationData={data} verificationTrigger={setEmail} />
                <InputField name="password" placeholder="Hasło" type="password" />

                {marketingSms && <PhoneFieldControlled name="phone" codes={countryCodes} placeholder="Telefon" />}

                <UserMktOptins showForm initialValues={registerFormDefaultValues} formId={`${currentViewType}_register`} />

                <Button
                    className={submit}
                    type="submit"
                    variant="filled_primary"
                    isLoading={methods.formState.isSubmitting || isLoading || isFetching}
                    disabled={disableSubmitButton}
                    onClick={() => {
                        if (amplitudePlacement) {
                            trackAmplitudeAuthEvent("sign_up_click", {signUpMethod: "internal", signUpPlacement: amplitudePlacement});
                        }
                    }}
                >
                    {registerButtonText}
                </Button>
                <div className={leadTerms}>
                    <LeadButtonTerms buttonText={registerButtonText} />
                    <LeadRodoTerms />
                </div>
            </form>
        </FormProviderRHF>
    );
};

const form = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 2
});

const submit = css({
    width: "100%",
    marginTop: 1
});

const leadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2,
    textAlign: "center"
});
import {useEffect, useState} from "react";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import type {IAppError} from "@pg-mono/request-state";
import {EmailInputWithVerification} from "@pg-rp/email-with-verification";
import {useEmailVerificationApiError} from "@pg-rp/email-with-verification/src/hooks/use_email_verification_api_error";

import {LeadButtonTerms} from "../../../application/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../application/components/LeadRodoTerms";
import {useVerifyEmailQuery} from "../../../email_verification/api/email_verification_api";
import {addNotification} from "../../../notifications/state/notifications_slice";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {userSignUpHit} from "../../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../../tracking/types/AmplitudeAuthPlacement";
import {UserMktOptins} from "../../../user/components/UserMktOptins";
import {UserAccountType} from "../../../user/types/UserAccountType";
import {authSucceeded} from "../../actions/auth_succeeded_event";
import {emailSignUp} from "../../api/email_sign_up";
import {registerFormDefaultValues} from "../../constants/register_form_default_values";
import {registerFormValidationSchema} from "../../constants/register_form_validation_schema";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../../tracking/auth_analytics";
import {trackAmplitudeAuthEvent} from "../../tracking/track_amplitude_auth_event";
import {AuthActionType} from "../../types/AuthActionType";
import type {IRegister} from "../../types/IRegister";

interface IProps {
    onSuccessfulSubmit?: () => void;
    userSource: UserSource;
    gtmSourceSection?: string;
    gtmEventData?: () => void;
    amplitudePlacement?: AmplitudeAuthPlacement | null;
}

const registerButtonText = "Zarejestruj się";

export const RegisterForm = (props: IProps) => {
    const {onSuccessfulSubmit, userSource, gtmSourceSection, gtmEventData, amplitudePlacement} = props;
    const dispatch = useAppDispatch();

    const [email, setEmail] = useState<string | null>(null);
    const {data, error, isLoading, isFetching} = useVerifyEmailQuery({email: email || ""}, {skip: !email});
    const apiError = useEmailVerificationApiError({errorData: error});
    const currentViewType = useAppSelector((state) => state.viewType.current);
    const countryCodes = useAppSelector((state) => state.variables.data?.country_codes ?? []);

    const methods = useFormRHF<IRegister>({
        defaultValues: registerFormDefaultValues,
        resolver: yupResolver(registerFormValidationSchema),
        mode: "onChange"
    });
    const ignoreVerification = !!methods.formState.defaultValues?.email && !email;
    const disableSubmitButton = (!ignoreVerification && !data) || methods.formState.isSubmitting || !methods.formState.isValid || isLoading || isFetching;

    const marketingSms = methods.watch("marketing_sms");

    useEffect(() => {
        if (!apiError.isValid) {
            methods.setError("email", {message: apiError.message});
        }
    }, [apiError]);

    const onSubmit = async ({email, marketing_email, marketing_sms, password, phone}: IRegister) => {
        await emailSignUp({
            email,
            password,
            ...(marketing_sms ? {phone: `${phone.code} ${phone.number}`} : {}),
            type: UserAccountType.LOCAL,
            marketing_sms,
            marketing_email
        })
            .then(async (response) => {
                authAnalytics.gtm.emailAuthEvent({
                    event: GAAuthEventName.SIGN_UP,
                    eventCategory: GAAuthEventCategory.REGISTRATION_FORM,
                    source: gtmSourceSection ?? null,
                    userSource
                });

                dispatch(
                    authSucceeded({
                        type: AuthActionType.REGISTRATION,
                        userSource,
                        gtmSourceSection,
                        registrationMethod: "email"
                    })
                );
                if (amplitudePlacement) {
                    trackAmplitudeAuthEvent("signed_up", {signUpMethod: "internal", signUpPlacement: amplitudePlacement});
                }
                userSignUpHit(userSource, response, currentViewType);
                gtmEventData?.();
                onSuccessfulSubmit?.();
            })
            .catch((error: {appError?: IAppError}) => {
                if (error.appError?.fieldErrors) {
                    for (const field of Object.keys(error.appError.fieldErrors)) {
                        const message = error.appError.fieldErrors[field];

                        methods.setError(field as keyof IRegister, {
                            message: Array.isArray(message) ? message.join(", ") : message
                        });
                    }
                }

                if (error.appError?.nonFieldErrors && error.appError.nonFieldErrors.length) {
                    dispatch(addNotification({notification: {content: error.appError.nonFieldErrors.join(" "), type: "failure"}}));
                }
            });
    };

    return (
        <FormProviderRHF {...methods}>
            <form className={form} onSubmit={methods.handleSubmit(onSubmit)}>
                <EmailInputWithVerification verificationData={data} verificationTrigger={setEmail} />
                <InputField name="password" placeholder="Hasło" type="password" />

                {marketingSms && <PhoneFieldControlled name="phone" codes={countryCodes} placeholder="Telefon" />}

                <UserMktOptins showForm initialValues={registerFormDefaultValues} formId={`${currentViewType}_register`} />

                <Button
                    className={submit}
                    type="submit"
                    variant="filled_primary"
                    isLoading={methods.formState.isSubmitting || isLoading || isFetching}
                    disabled={disableSubmitButton}
                    onClick={() => {
                        if (amplitudePlacement) {
                            trackAmplitudeAuthEvent("sign_up_click", {signUpMethod: "internal", signUpPlacement: amplitudePlacement});
                        }
                    }}
                >
                    {registerButtonText}
                </Button>
                <div className={leadTerms}>
                    <LeadButtonTerms buttonText={registerButtonText} />
                    <LeadRodoTerms />
                </div>
            </form>
        </FormProviderRHF>
    );
};

const form = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 2
});

const submit = css({
    width: "100%",
    marginTop: 1
});

const leadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2,
    textAlign: "center"
});
